import React from "react";
import { Wind, Eye, Compass, Waves, Activity, ShieldCheck } from "lucide-react";
import ApplicationTemplate from "@/components/applications/ApplicationTemplate";

const AnxietyRegulation = () => (
  <ApplicationTemplate
    pageKey="anxiety-regulation"
    breadcrumbLabel="Anxiety & Regulation"
    overline="Application · Anxiety & Regulation"
    headline="Anxiety is a state your body knows how to produce."
    headlineAccent="States can be retrained."
    lead="Anxiety is often described as a thought problem. Physiologically, it is a threat-detection system operating at a lowered activation threshold. Adaptive Physiology treats it as a trainable set-point, not a fixed trait."
    loopHighlight="adaptation"
    challenge={{
      title: "The threat-detection system learned to fire early.",
      paragraphs: [
        "Repeated exposure to unpredictable stress, inadequate recovery, or overwhelming input can down-regulate the parasympathetic branch and up-tune the sympathetic one. The result is a physiology that assumes threat first and asks questions later.",
        "The mind experiences this as anxiety. The nervous system experiences it as competence — an efficient response to a pattern it has learned.",
      ],
    }}
    traditionalGap={{
      title: "Top-down approaches meet a bottom-up problem.",
      paragraphs: [
        "Cognitive strategies, medication, and therapeutic conversation are important tools — many people benefit from them. But anxiety frequently runs faster than cognition; it fires before the thinking system arrives.",
        "Adaptive Physiology is not a replacement for these approaches. It is a physiological layer added underneath — retraining the system that generates the signal in the first place.",
      ],
    }}
    perspective={{
      title: "Change what the body assumes. The mind will follow.",
      paragraphs: [
        "Rhythmic aerobic work, breath-led vagal loading, interoceptive practice, and environmental design are used to raise the activation threshold and restore parasympathetic access.",
        "Over time, the physiology stops volunteering an emergency response to non-emergencies. The nervous system does not forget how to protect — it learns when protection is warranted.",
      ],
    }}
    frameworkApplication={[
      {
        title: "Baseline the arousal profile",
        body: "HRV, resting HR, breathing pattern, sleep, interoceptive awareness, and situational triggers are captured.",
      },
      {
        title: "Prescribe the regulation stimulus",
        body: "Aerobic base, respiratory pacing, exposure-graded movement, and environmental cues chosen to widen the window of tolerance without provoking it.",
      },
      {
        title: "Reinforce the felt sense of safety",
        body: "Consistent, low-dose exposure to a regulated physiology teaches the system a new default. Repetition is where anxiety loses its grip.",
      },
      {
        title: "Retrain the activation threshold",
        body: "The system no longer treats ordinary stimuli as emergencies. The regulated state becomes the default, not the exception.",
      },
    ]}
    capacities={[
      { icon: Wind, label: "Vagal access", body: "Physiological path back to a settled state." },
      { icon: Waves, label: "Interoceptive capacity", body: "Ability to read the body’s signals accurately, not reactively." },
      { icon: Eye, label: "Threshold headroom", body: "More room between an input and an anxious response." },
      { icon: Compass, label: "Contextual regulation", body: "The right physiological response for the actual situation." },
      { icon: Activity, label: "Adaptive recovery", body: "Faster return to baseline after activation." },
      { icon: ShieldCheck, label: "Felt safety", body: "A body that can register safety, not just detect threat." },
    ]}
  />
);
export default AnxietyRegulation;
